import React from 'react';
import { Link } from 'react-router-dom';

export type NavItem = {
  key: string;
  label: string;
  href: string;
  icon?: React.ReactNode;
  active?: boolean;
  disabled?: boolean;
  badge?: string;
  trailing?: React.ReactNode;
  onClick?: () => void;
};

export type NavBarProps = {
  items: NavItem[];
  header?: React.ReactNode;
  footer?: React.ReactNode;
  collapsed?: boolean;
  className?: string;
  listClassName?: string;
  itemClassName?: string;
  /**
   * Base classes for nav items (applied to all items)
   */
  itemBaseClassName?: string;
  /**
   * Classes for active nav items
   */
  itemActiveClassName?: string;
  /**
   * Classes for inactive nav items
   */
  itemInactiveClassName?: string;
  /**
   * Classes for disabled nav items
   */
  itemDisabledClassName?: string;
  /**
   * Classes for badge elements
   */
  badgeClassName?: string;
  ariaLabel?: string;
};

/**
 * Stateless vertical navigation primitive.
 * 
 * This component handles structure and Link rendering. Hosts provide:
 * - Data (items, active state)
 * - Styling (via className props)
 * - Behavior (collapsed state, header/footer)
 * 
 * No hooks, no navigation logic, no styling decisions - pure presentation.
 */
export function NavBar({
  items,
  header,
  footer,
  collapsed = false,
  className = '',
  listClassName = '',
  itemClassName = '',
  itemBaseClassName = 'flex items-center gap-3 rounded-lg px-3 py-2 transition-colors text-sm font-medium',
  itemActiveClassName = '',
  itemInactiveClassName = '',
  itemDisabledClassName = 'opacity-50 cursor-not-allowed',
  badgeClassName = '',
  ariaLabel = 'Navigation',
}: NavBarProps) {
  return (
    <nav className={`flex flex-col h-full ${className}`} aria-label={ariaLabel}>
      {header ? <div className="flex-shrink-0">{header}</div> : null}

      <ul className={`flex-1 space-y-1 mt-2 ${listClassName}`} role="list">
        {items.map((item) => {
          const Wrapper = item.disabled ? 'div' : Link;
          const stateClassName = item.disabled
            ? itemDisabledClassName
            : item.active
              ? itemActiveClassName
              : itemInactiveClassName;

          return (
            <li key={item.key}>
              <Wrapper
                to={item.disabled ? '' : item.href}
                onClick={item.disabled ? undefined : item.onClick}
                className={`${itemBaseClassName} ${stateClassName} ${itemClassName} ${collapsed ? 'justify-center px-0' : ''}`}
                title={collapsed && !item.disabled ? item.label : undefined}
              >
                {item.icon ? (
                  <span 
                    className="flex-shrink-0 w-5 h-5 flex items-center justify-center"
                    style={{ marginRight: collapsed ? '0px' : '8px' }}
                  >
                    {item.icon}
                  </span>
                ) : (
                  <span className={`${collapsed ? 'w-0' : 'w-5'}`} />
                )}
                {!collapsed && (
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="truncate">{item.label}</span>
                    {item.badge ? (
                      <span className={`text-[11px] px-2 py-[2px] rounded-full ${badgeClassName}`}>
                        {item.badge}
                      </span>
                    ) : null}
                    {item.trailing ? <span className="ml-auto">{item.trailing}</span> : null}
                  </div>
                )}
              </Wrapper>
            </li>
          );
        })}
      </ul>

      {footer ? <div className="flex-shrink-0 mt-2">{footer}</div> : null}
    </nav>
  );
}

